// "To Get The Most From Your Fitting..." form — shown alongside the
// confirmation block. Captures ball-fitting upsell, phone, SMS opt-in,
// and (in the design) dexterity. Designed to slot into the right column
// of the CongratsScreen.
//
// Design comp reference: Screenshot 2026-05-25 6:18:58.

import styles from '../styles/widget.module.css';
import { emit } from '../state/events';
import { submitPostBookingForm, ApiError } from '../api/client';
import type { BookingAction, BookingState } from '../state/machine';
import Checkmark from '../assets/icons/Checkmark';
import { AlertMessage } from '../components/AlertMessage';

interface GetSetUpProps {
  state: BookingState;
  dispatch: React.Dispatch<BookingAction>;
}

export function GetSetUp({ state, dispatch }: GetSetUpProps) {
  const canSubmit =
    state.postBookingPhone.trim().length >= 10 && !state.postBookingSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    if (!state.confirmation) return;

    dispatch({ type: 'POST_BOOKING_SUBMIT_START' });

    try {
      await submitPostBookingForm({
        bookingId: state.confirmation.id,
        // SF /PostAppointmentUpdate verifies identity by matching the
        // supplied email to the Account on file before patching anything.
        email: state.email.trim(),
        ballFittingInterest: state.postBookingBallFitting,
        phone: state.postBookingPhone.trim(),
        smsOptIn: state.postBookingSmsOptIn,
        // Newsletter opt-in is captured at lead capture (the "Contact me with
        // offers" checkbox) and propagated here so the post-booking write
        // persists the user's full marketing-consent set in one shot.
        newsletterOptIn: state.promoOptIn,
      });
      dispatch({ type: 'POST_BOOKING_SUBMIT_SUCCESS' });
      emit('cc:booking-widget:post-booking-completed', {
        bookingId: state.confirmation.id,
      });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `Couldn’t send to your fitter (${err.status}). ${err.message}`
          : err instanceof Error
            ? err.message
            : 'Couldn’t send to your fitter right now.';
      dispatch({ type: 'POST_BOOKING_SUBMIT_ERROR', payload: msg });
    }
  };

  return (
    <div className={styles.getSetUpAdditionalFields}>
      <h4>To get the most from your fitting…</h4>

      <div className={styles.field} style={{ marginBottom: 16 }}>
        <label className={styles.label}>
          Do you want your swing data to be used to find the best ball for your game? ($25 additional)
        </label>
        <button
          type="button"
          className={`${styles.handednessButton} ${state.postBookingBallFitting ? styles.selected : ''}`}
          data-cc-track="toggle"
          data-cc-track-id="ball_fitting_interest"
          onClick={() =>
            dispatch({
              type: 'POST_BOOKING_FIELD',
              payload: { postBookingBallFitting: !state.postBookingBallFitting },
            })
          }
        >
          <span className={`${styles.buttonCheckbox} ${state.postBookingBallFitting ? styles.checked : ""}`}>
            {state.postBookingBallFitting ? <Checkmark /> : null}
          </span>
          <span>I would like a golf ball fitting</span>
        </button>
      </div>

      <div className={styles.field} style={{ marginBottom: 16 }}>
        <label className={styles.label} htmlFor="cc-phone">
          Your phone number for a pre-fit call
        </label>
        <input
          id="cc-phone"
          name="tel"
          className={styles.input}
          type="tel"
          autoComplete="tel"
          inputMode="tel"
          autoCorrect="off"
          spellCheck={false}
          value={state.postBookingPhone}
          placeholder="(555) 555-5555"
          onChange={(e) =>
            dispatch({ type: 'POST_BOOKING_FIELD', payload: { postBookingPhone: e.target.value } })
          }
        />
      </div>

      <div className={styles.field} style={{ marginBottom: 20 }}>
        <label className={styles.checkboxRow}>
          <input
            type="checkbox"
            checked={state.postBookingSmsOptIn}
            onChange={(e) =>
              dispatch({
                type: 'POST_BOOKING_FIELD',
                payload: { postBookingSmsOptIn: e.target.checked },
              })
            }
          />
          <span>Opt in to receive marketing SMS updates and allow your fitter to contact you before your fitting.</span>
        </label>
      </div>

      <button
        type="button"
        className={`${styles.button} ${styles.sendToFitterButton} ${styles.stepButtonMobile}`}
        disabled={!canSubmit}
        data-cc-track="cta"
        data-cc-track-id="get_set_up_submit"
        onClick={handleSubmit}
      >
        {state.postBookingSubmitting ? (
          <>
            <span className={styles.spinner} />
            Sending…
          </>
        ) : (
          'Send to Fitter'
        )}
      </button>

      {state.postBookingError && (
        <AlertMessage title='Couldn’t send to your fitter' description={state.postBookingError} />
      )}
    </div>
  );
}
